<template>
  <DetailsAccordion
    :error-description="i18n(translations.errorDescription)"
    :status
    @refresh="refresh"
  >
    <template #icon>
      <IconInfo />
    </template>
    <template #title>
      {{ i18n(translations.title, { order: orderline?.orderId }) }}
    </template>
    <template #description>
      <span data-test="description">
        {{ i18n(orderStateLabelTranslation) }}
      </span>
    </template>
    <template #body>
      <template v-if="orderline">
        <ul class="space-y-20">
          <li class="space-y-2">
            <p>
              {{
                i18n(translations.orderDate, {
                  orderDate: new Date(orderline.creationDate),
                })
              }}
            </p>

            <ShippingList data-test="shipping-list" :orderline />
          </li>

          <RefundList
            v-if="refundedOrderState"
            data-test="refund-list"
            :refunds="orderline.refundLists.successfulRefunds"
          />

          <li v-if="orderline.state === ORDERLINE_STATES.CANCELLED">
            {{
              i18n(translations.cancelDate, {
                cancelDate: new Date(orderline.refundDate),
              })
            }}
          </li>
        </ul>
        <div class="mt-16 flex gap-8 sm:flex-wrap">
          <div v-if="!isEmpty(orderline.delivery?.trackingUrl)" class="mb-2">
            <RevButton
              :aria-label="i18n(translations.track)"
              :icon="IconExternalLink"
              size="small"
              target="_blank"
              :title="i18n(translations.track)"
              :to="orderline.delivery.trackingUrl"
              variant="secondary"
            >
              {{ i18n(translations.track) }}
            </RevButton>
          </div>
          <div v-if="!isEmpty(orderline.deliveryFormUrl)" class="mb-2">
            <RevButton
              :aria-label="i18n(translations.downloadDeliveryForm)"
              :icon="IconDownload"
              size="small"
              target="_blank"
              :title="i18n(translations.downloadDeliveryForm)"
              :to="orderline.deliveryFormUrl"
              variant="secondary"
            >
              {{ i18n(translations.downloadDeliveryForm) }}
            </RevButton>
          </div>
        </div>
      </template>
    </template>
  </DetailsAccordion>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { IconDownload } from '@ds/icons/IconDownload'
import { IconExternalLink } from '@ds/icons/IconExternalLink'
import { IconInfo } from '@ds/icons/IconInfo'

import { ORDERLINE_STATES } from '~/api/care-folders/types/orderline-states.types'
import { useOrderlineDetails } from '~/scopes/customer-care/pages/request/composables/useOrderlineDetails'

import DetailsAccordion from '../DetailsAccordion/DetailsAccordion.vue'

import translations from './OrderHistory.translations'
import RefundList from './RefundList/RefundList.vue'
import ShippingList from './ShippingList/ShippingList.vue'

const {
  orderDetails: {
    data: { orderline },
    status,
    refresh,
  },
} = useOrderlineDetails()

const refundedOrderState = computed(
  () =>
    ORDERLINE_STATES.REFUNDED_POST_DELIVERY === orderline.value?.state ||
    ORDERLINE_STATES.REFUNDED_PRE_DELIVERY === orderline.value?.state,
)

const orderStateLabelTranslation = computed(() => {
  switch (orderline.value?.state) {
    case ORDERLINE_STATES.REFUNDED_POST_DELIVERY:
    case ORDERLINE_STATES.REFUNDED_PRE_DELIVERY:
      return translations.refunded
    case ORDERLINE_STATES.CANCELLED:
      return translations.cancelled
    case ORDERLINE_STATES.VALIDATED:
      return translations.validated
    case ORDERLINE_STATES.PAID:
      return translations.paid
    case ORDERLINE_STATES.SHIPPED:
      if (orderline.value.delivery?.delivered) {
        if (!isEmpty(orderline.value.delivery.deliveryDate)) {
          return translations.delivered
        }

        return translations.notTracked
      }

      return translations.shipped
    default:
      return translations.stateUnknown
  }
})

const i18n = useI18n()
</script>
